import { FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, NetworkError } from "../api/client";
import { correctMember, getMemberDetail, updateMemberContact, type MemberCorrectionPatch, type MemberProfile } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { normalizePreferredName } from "../api/normalizePreferredName";
import { normalizeBrazilianPhone } from "../api/identityValidation";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";
import { TextField } from "../components/TextField";
import { useDirtyFormProtection } from "../hooks/useDirtyFormProtection";

type View = { type: "loading" } | { type: "ready"; profile: MemberProfile } | { type: "error"; message: string };

export function MemberEditPage() {
  const { memberId } = useParams();
  const { state, expireSession } = useAuth();
  const [view, setView] = useState<View>({ type: "loading" });
  const [form, setForm] = useState<MemberCorrectionPatch>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const isSelf = state.status === "authenticated" && state.memberId === memberId;
  const canCorrect = state.status === "authenticated" && Boolean(state.sessionContext?.capabilities.write_members);
  const canEdit = canCorrect || isSelf;
  const isSelfReadOnly = isSelf && !canCorrect;
  const guardDirtyNavigation = useDirtyFormProtection(dirty && !saved);

  useEffect(() => {
    if (state.status !== "authenticated" || !memberId) return;
    let cancelled = false;
    void getMemberDetail(state.accessToken, memberId)
      .then((profile) => {
        if (!cancelled) {
          setForm({ full_name: profile.full_name, preferred_name: profile.preferred_name ?? "", email: profile.email ?? "", phone: profile.phone ?? "", birth_date: profile.birth_date ?? "" });
          setDirty(false);
          setView({ type: "ready", profile });
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 401) { expireSession(); return; }
        setView({ type: "error", message: cause instanceof ApiError || cause instanceof NetworkError ? cause.message : "Não foi possível carregar o cadastro." });
      });
    return () => { cancelled = true; };
  }, [expireSession, memberId, state.status === "authenticated" ? state.accessToken : null]);

  if (state.status !== "authenticated" || !memberId) return null;
  const accessToken = state.accessToken;
  const currentMemberId = memberId;
  if (view.type === "loading") return <main className="operational-page"><p className="members-state">Carregando cadastro…</p></main>;
  if (view.type === "error") return <main className="operational-page"><Link className="page-back" to={`/associados/${memberId}`}>Voltar para ficha</Link><StatusBanner tone="error">{view.message}</StatusBanner></main>;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true); setSaved(false);
    const phone = normalizeBrazilianPhone(form.phone ?? "");
    if (!phone) {
      setSaving(false);
      setView({ type: "error", message: "Informe um telefone brasileiro válido com DDD." });
      return;
    }
    try {
      const preferredName = normalizePreferredName(form.preferred_name);
      if (canCorrect) {
        await correctMember(accessToken, currentMemberId, { ...form, phone, preferred_name: preferredName });
      } else {
        await updateMemberContact(accessToken, currentMemberId, { preferred_name: preferredName, phone });
      }
      setSaved(true);
      setDirty(false);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) { expireSession(); return; }
      setView({ type: "error", message: cause instanceof ApiError || cause instanceof NetworkError ? cause.message : "Não foi possível salvar as alterações." });
    } finally { setSaving(false); }
  }

  function update(field: keyof MemberCorrectionPatch, value: string) { setSaved(false); setDirty(true); setForm((current) => ({ ...current, [field]: value })); }

  return (
    <main className="operational-page">
      <Link className="page-back" to={`/associados/${memberId}`} onClick={guardDirtyNavigation}>Voltar para ficha</Link>
      <section className="form-card" aria-labelledby="member-edit-title">
        <p className="members-page__eyebrow">{canCorrect ? "Secretaria e DAF" : "Meu cadastro"}</p>
        <h1 id="member-edit-title">{canCorrect ? "Corrigir cadastro" : "Atualizar contato"}</h1>
        <p className="form-card__intro">{canCorrect ? "A correção administrativa não altera CPF, categoria, efetivação ou usuário de acesso." : "Você pode atualizar apenas seu nome social e telefone."}</p>
        {saved && <StatusBanner tone="success">Alterações salvas com sucesso.</StatusBanner>}
        {!canEdit && <StatusBanner tone="warning">Esta tela é somente informativa para o seu perfil.</StatusBanner>}
        <form onSubmit={submit} noValidate>
          <div className="form-grid">
            <TextField label="Nome completo" required={canCorrect} disabled={!canEdit} readOnly={isSelfReadOnly} hint={isSelfReadOnly ? "Nome completo só pode ser alterado pela Secretaria e DAF." : undefined} value={form.full_name ?? ""} onChange={(event) => update("full_name", event.target.value)} />
            <TextField label="Nome social" disabled={!canEdit} value={form.preferred_name ?? ""} onChange={(event) => update("preferred_name", event.target.value)} />
            <TextField label="E-mail" type="email" disabled={!canEdit} readOnly={isSelfReadOnly} hint={isSelfReadOnly ? "E-mail só pode ser alterado pela Secretaria e DAF." : undefined} value={form.email ?? ""} onChange={(event) => update("email", event.target.value)} />
            <TextField label="Telefone" type="tel" disabled={!canEdit} value={form.phone ?? ""} onChange={(event) => update("phone", event.target.value)} />
            <TextField label="Data de nascimento" type="date" disabled={!canEdit} readOnly={isSelfReadOnly} hint={isSelfReadOnly ? "Data de nascimento só pode ser alterada pela Secretaria e DAF." : undefined} value={form.birth_date ?? ""} onChange={(event) => update("birth_date", event.target.value)} />
          </div>
          {canEdit && <div className="form-actions"><Button type="submit" loading={saving}>Salvar alterações</Button></div>}
        </form>
      </section>
    </main>
  );
}
